/**
 * RegistrationForm - Organism component
 * Complete registration form with validation
 * WCAG 2.1 AA compliant
 */

import React, { useState } from 'react';
import { FormField, Alert, Input } from '@kisanlink/ui-commons';
import { Button } from '@/components/atoms';
import { authClient } from '@/services/clients';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from '@/hooks/useRedux';
import { setCredentials } from '@/store/slices/authSlice';
import type { AuthLoginResponse } from 'auth-service';

export interface RegistrationFormProps {
  /**
   * Callback when registration is successful
   */
  onSuccess?: () => void;
}

export const RegistrationForm: React.FC<RegistrationFormProps> = ({
  onSuccess,
}) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [countryCode, setCountryCode] = useState('+91');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [validationErrors, setValidationErrors] = useState<{
    name?: string;
    phoneNumber?: string;
    password?: string;
    confirmPassword?: string;
  }>({});

  const validate = () => {
    const errors: {
      name?: string;
      phoneNumber?: string;
      password?: string;
      confirmPassword?: string;
    } = {};

    if (!name || name.trim().length < 2) {
      errors.name = 'Name must be at least 2 characters';
    }

    if (!phoneNumber) {
      errors.phoneNumber = 'Phone number is required';
    } else if (!/^\d{10}$/.test(phoneNumber)) {
      errors.phoneNumber = 'Please enter a valid 10-digit phone number';
    }

    if (!password) {
      errors.password = 'Password is required';
    } else if (password.length < 6) {
      errors.password = 'Password must be at least 6 characters';
    }

    if (!confirmPassword) {
      errors.confirmPassword = 'Please confirm your password';
    } else if (password !== confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    try {
      setIsLoading(true);
      setApiError(null);

      // Register returns either { user_id } or AuthLoginResponse wrapped in { data, success, timestamp }
      const response = await authClient.auth.register({
        country_code: countryCode,
        phone_number: phoneNumber,
        password,
        name: name.trim(),
      });

      // Access the nested data
      const regData = (response as any).data as AuthLoginResponse | { user_id: string };

      // Check if registration returns tokens (auto-login)
      if (regData && 'access_token' in regData && regData.access_token) {
        console.log('[Registration] Auto-login successful:', {
          user: regData.user?.id,
          userName: regData.user?.name,
          hasToken: !!regData.access_token,
        });

        dispatch(
          setCredentials({
            accessToken: regData.access_token,
            refreshToken: regData.refresh_token,
            user: regData.user,
          })
        );

        // Success - navigate or callback
        if (onSuccess) {
          onSuccess();
        } else {
          navigate('/');
        }
      } else {
        // Registration successful but need to login
        console.log('[Registration] Registration successful, navigate to login');
        if (onSuccess) {
          onSuccess();
        } else {
          navigate('/login');
        }
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Registration failed';
      setApiError(errorMessage);
      console.error('Registration failed:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const errorMessage = apiError || null;

  return (
    <form onSubmit={handleSubmit} className="w-full max-w-md space-y-6" noValidate>
      {errorMessage && (
        <Alert variant="error" onClose={() => {}}>
          {errorMessage}
        </Alert>
      )}

      <FormField
        label="Full Name"
        error={validationErrors.name}
        required
      >
        <Input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          autoComplete="name"
          disabled={isLoading}
          placeholder="Enter your full name"
        />
      </FormField>

      <div className="space-y-2">
        <label htmlFor="phone" className="block text-sm font-medium text-neutral-700">
          Phone Number
          <span className="text-error-600 ml-1" aria-label="required">*</span>
        </label>
        <div className="flex gap-2">
          <input
            type="text"
            value={countryCode}
            onChange={(e) => setCountryCode(e.target.value)}
            disabled={isLoading}
            className="w-20 px-3 py-2 rounded-md border border-neutral-300 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-neutral-100"
            placeholder="+91"
          />
          <FormField error={validationErrors.phoneNumber}>
            <Input
              type="tel"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              autoComplete="tel"
              disabled={isLoading}
              placeholder="1234567890"
            />
          </FormField>
        </div>
      </div>

      <FormField
        label="Password"
        error={validationErrors.password}
        required
        helperText="Password must be at least 6 characters long"
      >
        <Input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="new-password"
          disabled={isLoading}
          placeholder="Create a password (min 6 characters)"
        />
      </FormField>

      <FormField
        label="Confirm Password"
        error={validationErrors.confirmPassword}
        required
      >
        <Input
          type="password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          autoComplete="new-password"
          disabled={isLoading}
          placeholder="Re-enter your password"
        />
      </FormField>

      <Button
        type="submit"
        variant="primary"
        size="lg"
        loading={isLoading}
        disabled={isLoading}
        className="w-full"
      >
        Create Account
      </Button>

      <div className="text-center text-sm text-neutral-600">
        Already have an account?{' '}
        <button
          type="button"
          onClick={() => navigate('/login')}
          className="font-medium text-primary-600 hover:text-primary-500 focus:outline-none focus:underline"
        >
          Sign in
        </button>
      </div>
    </form>
  );
};
